import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Crown, Loader2, X, ImageIcon } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Upload() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [premium, setPremium] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [duration, setDuration] = useState(0);
  const [coverTime, setCoverTime] = useState(0);
  const [customCover, setCustomCover] = useState(null);
  const videoRef = useRef(null);
  const isVideo = file?.type.startsWith("video/");
  const canPremium = user?.is_creator && user?.price_tier;

  const pick = (f) => {
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) return toast.error("File too large (max 100MB)");
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setCustomCover(null);
    setDuration(0);
  };

  const seek = (t) => {
    setCoverTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const captureCover = () => new Promise((resolve) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return resolve(null);
    const c = document.createElement("canvas");
    const scale = Math.min(1, 1080 / v.videoWidth);
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(resolve, "image/jpeg", 0.85);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a video or photo first");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    fd.append("is_premium", premium ? "true" : "false");
    if (isVideo) {
      const cover = customCover || (await captureCover());
      if (cover) fd.append("cover", cover, "cover.jpg");
    }
    setBusy(true);
    try {
      await api.post("/posts", fd, { onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)) });
      toast.success("Posted!");
      nav(`/u/${user.username}`);
    } catch (err) {
      toast.error(formatErr(err));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-8 md:py-16">
      <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-[#FF2E4D]">New post</p>
      <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Drop something new</h1>
      <form onSubmit={submit} className="mt-10 grid gap-8 md:grid-cols-[320px_1fr]">
        <div className="relative aspect-[9/16] overflow-hidden rounded-md border border-dashed border-[#2A2E37] bg-[#121418]">
          {preview ? (
            <>
              {isVideo ? (customCover ? <img src={URL.createObjectURL(customCover)} alt="" className="h-full w-full object-cover" data-testid="upload-preview" />
                : <video ref={videoRef} src={preview} className="h-full w-full object-cover" muted playsInline preload="auto" data-testid="upload-preview"
                  onLoadedMetadata={(e) => { const d = e.target.duration || 0; setDuration(d); seek(Math.min(0.5, d / 2)); }} />)
                : <img src={preview} alt="" className="h-full w-full object-cover" data-testid="upload-preview" />}
              <button type="button" onClick={() => { setFile(null); setPreview(null); }} data-testid="upload-clear-btn"
                className="absolute right-3 top-3 rounded-sm bg-black/60 p-2"><X className="h-4 w-4" /></button>
              {isVideo && <span className="absolute left-3 top-3 rounded-sm bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-wider">Cover</span>}
            </>
          ) : (
            <label className="flex h-full cursor-pointer flex-col items-center justify-center gap-3 p-6 text-center transition-colors hover:bg-white/5"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}>
              <UploadCloud className="h-10 w-10 text-[#FF2E4D]" />
              <span className="font-display text-base font-semibold">Select video or photo</span>
              <span className="text-xs text-gray-400">MP4, MOV, WebM, JPG, PNG · up to 100MB</span>
              <input type="file" accept="video/*,image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="upload-file-input" />
            </label>
          )}
        </div>
        <div className="space-y-6">
          <div>
            <label className="text-sm font-semibold text-gray-300">Caption</label>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={500} data-testid="upload-caption-input"
              placeholder="Say something about this drop..." className="mt-2 min-h-[120px] w-full rounded-md border border-[#2A2E37] bg-white/5 px-4 py-3 text-sm outline-none focus:border-white/40" />
          </div>
          {isVideo && (
            <div className="rounded-md border border-[#2A2E37] bg-[#121418] p-5" data-testid="cover-picker">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Cover frame</p>
                <span className="font-mono text-xs tabular-nums text-gray-500" data-testid="cover-time">{customCover ? "Custom image" : `${coverTime.toFixed(1)}s`}</span>
              </div>
              <p className="mt-1 text-xs text-gray-400">Shown on your profile grid and before the video plays. Drag to pick a frame.</p>
              {!customCover && duration > 0 && (
                <Slider className="mt-4" min={0} max={Math.max(duration - 0.05, 0.1)} step={0.1} value={[coverTime]}
                  onValueChange={([v]) => seek(v)} data-testid="cover-slider" />
              )}
              <div className="mt-4 flex items-center gap-3 text-xs">
                <label className="flex cursor-pointer items-center gap-1.5 text-gray-300 hover:text-white">
                  <ImageIcon className="h-3.5 w-3.5" /> Upload a cover image
                  <input type="file" accept="image/*" className="hidden" data-testid="cover-image-input"
                    onChange={(e) => e.target.files?.[0] && setCustomCover(e.target.files[0])} />
                </label>
                {customCover && <button type="button" onClick={() => setCustomCover(null)} className="text-gray-500 hover:text-white" data-testid="cover-reset-btn">Use a video frame</button>}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-md border border-[#2A2E37] bg-[#121418] p-5">
            <div className="flex items-start gap-3">
              <Crown className="mt-0.5 h-5 w-5 text-[#FF2E4D]" />
              <div>
                <p className="font-semibold">Subscribers only</p>
                <p className="text-xs text-gray-400">
                  {canPremium ? "Non-subscribers see a blurred, locked teaser." : <>Turn on creator mode in <Link to={`/u/${user?.username}`} className="text-[#FF2E4D] hover:underline">your profile</Link> to sell content.</>}
                </p>
              </div>
            </div>
            <Switch checked={premium} onCheckedChange={setPremium} disabled={!canPremium} data-testid="upload-premium-switch" />
          </div>
          {busy && <Progress value={progress} className="h-2" data-testid="upload-progress" />}
          <button type="submit" disabled={busy} data-testid="upload-submit-btn" className="btn-neon flex w-full items-center justify-center gap-2 rounded-sm py-3.5 font-semibold text-white disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? `Uploading ${progress}%` : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}
